import type { InviteStatus } from "@teamna/shared";
import { useEffect, useRef, useState } from "react";
import {
  Button,
  CellField,
  CopyIcon,
  DataTable,
  ErrorState,
  FormRow,
  FormSection,
  InlineError,
  Notice,
  SkeletonList,
  StatTile,
  Switch,
  TextAreaField,
  showToast,
  type DataTableColumn,
} from "../../components/index.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useInvitePreview, useInviteStatus, useNudge, useSendInvites } from "@teamna/web-client/hooks/use-setup.ts";
import { formatDateTime } from "@teamna/web-client/lib/format.ts";
import { SaveBar, SetupFrame } from "./frame.tsx";
import { phoneText } from "../families/model.ts";

/**
 * Setup step 6: invite members (mobile spec 5.3, "Invite parents"), open once sessions and packages
 * exist (the frame shows why before that).
 *
 * The join code and link, the message preview the owner can edit, the two toggles (a private
 * message to each member, a nudge after three days), and Send. After a send: who joined, who did
 * not, and "Nudge the rest". The team gets personal invitations when the owner adds them (Team page), not this send. On staging nothing leaves the
 * API; the page says so rather than pretending.
 */
export function SetupInviteScreen() {
  return <SetupFrame step="invite_sent">{(businessId) => <InviteBody businessId={businessId} />}</SetupFrame>;
}

type NotJoined = InviteStatus["notJoined"][number];

const notJoinedColumns: DataTableColumn<NotJoined>[] = [
  {
    id: "name",
    header: "Name",
    cell: (row) => <span className="font-semibold text-ink">{row.displayName}</span>,
    sortValue: (row) => row.displayName,
  },
  { id: "phone", header: "Mobile", cell: (row) => phoneText(row.phoneE164) },
  {
    id: "nudged",
    header: "Nudged",
    cell: (row) => (row.nudgedAt ? formatDateTime(row.nudgedAt) : "Not yet"),
    hideOnPhone: true,
  },
];

function InviteBody({ businessId }: { businessId: string }) {
  const { label } = useLabels();
  const members = label("member", { plural: true });
  const preview = useInvitePreview(businessId);
  const status = useInviteStatus(businessId);
  const send = useSendInvites(businessId);
  const nudge = useNudge(businessId);
  // Null until the owner types: the preview's own text shows until then.
  const [edited, setMessage] = useState<string | null>(null);
  const message = edited ?? preview.data?.messageBody ?? "";
  const [dmEach, setDmEach] = useState(true);
  const [nudgeLater, setNudgeLater] = useState(true);
  const [manualCopy, setManualCopy] = useState(false);

  if (preview.isLoading || status.isLoading) return <SkeletonList rows={3} />;
  if (preview.error || !preview.data) {
    return (
      <ErrorState error={preview.error ?? new Error("missing")} onRetry={() => void preview.refetch()} />
    );
  }

  const data = preview.data;
  const sent = Boolean(status.data?.sentAt);

  /**
   * Some browsers refuse the clipboard (an insecure page, a strict setting, an embedded view). The
   * owner must always get the link, so a refusal opens the link in a field to select and copy,
   * with the share sheet where the browser has one.
   */
  async function copyLink() {
    try {
      if (!navigator.clipboard) throw new Error("No clipboard");
      await navigator.clipboard.writeText(data.url);
      showToast("Link copied");
    } catch {
      setManualCopy(true);
    }
  }

  function submit() {
    send.mutate(
      { messageBody: message.trim(), dmEach, nudge: nudgeLater },
      {
        onSuccess: (result) =>
          showToast(
            result.simulated
              ? `${result.sent} ${result.sent === 1 ? "invite" : "invites"} recorded, not sent. WhatsApp is not connected yet.`
              : `${result.sent} ${result.sent === 1 ? "invite" : "invites"} sent`,
          ),
      },
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-2 rounded-card bg-accent px-5 py-5 text-inverse shadow-card sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-[11px] font-bold tracking-[0.12em] uppercase opacity-85">Join code</p>
          <p className="text-[32px] leading-tight font-semibold">{data.code}</p>
          <p className="truncate text-[14px] opacity-90">{data.url}</p>
        </div>
        <Button variant="secondary" onClick={() => void copyLink()}>
          <span className="inline-flex items-center gap-2">
            <CopyIcon size={16} /> Copy link
          </span>
        </Button>
      </section>

      {manualCopy ? <CopyByHand url={data.url} code={data.code} /> : null}

      {sent && status.data ? (
        <section className="flex flex-col gap-4">
          <h2 className="text-[19px]">Who has joined</h2>
          <p className="-mt-2 text-[14px] text-ink-2">Sent {formatDateTime(status.data.sentAt!)}.</p>
          <div className="grid grid-cols-2 gap-3 sm:max-w-[420px]">
            <StatTile
              label="Joined"
              value={`${status.data.joined} of ${status.data.invited}`}
              tone={status.data.joined > 0 ? "good" : "neutral"}
            />
            <StatTile label="Not yet" value={status.data.notJoined.length} />
          </div>
          <DataTable
            caption={`${label("member", { plural: true, capital: true })} who have not joined`}
            columns={notJoinedColumns}
            rows={status.data.notJoined}
            rowKey={(row) => row.id}
            empty={{ title: "Everyone is in", message: `Every ${label("member")} on your list has joined.` }}
            countLabel={(count) => `${count} not joined`}
          />
          {status.data.notJoined.length > 0 ? (
            <div>
              <Button
                variant="secondary"
                loading={nudge.isPending}
                onClick={() =>
                  nudge.mutate(undefined, {
                    onSuccess: (result) =>
                      showToast(
                        result.simulated
                          ? `${result.sent} ${result.sent === 1 ? "nudge" : "nudges"} recorded, not sent. WhatsApp is not connected yet.`
                          : `${result.sent} nudged`,
                      ),
                  })
                }
              >
                Nudge the rest
              </Button>
              {nudge.error ? (
                <div className="mt-3">
                  <InlineError error={nudge.error} />
                </div>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}

      <FormSection
        title={sent ? "Send again" : "The message"}
        description={`From ${data.ownerName}. You can change any word.`}
      >
        <FormRow label="Message" hint="It holds your join code and link.">
          <TextAreaField
            label="Message"
            rows={8}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
          />
        </FormRow>
        <FormRow
          label={`Also send a private message to each ${label("member")}`}
          hint="People cannot reply in an announcements group, so a private message reaches them too."
        >
          <Switch
            label={`Also send a private message to each ${label("member")}`}
            checked={dmEach}
            onChange={setDmEach}
          />
        </FormRow>
        <FormRow label="Nudge after 3 days" hint="A reminder to anyone who has not joined after three days.">
          <Switch label="Nudge after 3 days" checked={nudgeLater} onChange={setNudgeLater} />
        </FormRow>
        <FormRow label="Who gets it">
          <p className="pt-2 text-[14px] text-ink-2">
            {data.membersToInvite} {data.membersToInvite === 1 ? label("member") : members} on your list. Your team gets
            their own invitations from the Team page.
          </p>
        </FormRow>
      </FormSection>

      {data.simulated ? (
        <Notice tone="info" title="WhatsApp is not connected yet">
          Send records the invite for the join tracking, but no message leaves Teamna. Copy the link and share
          it in your WhatsApp group.
        </Notice>
      ) : null}

      <SaveBar
        note={`${label("member", { plural: true, capital: true })} on your list get in at once when they join. Anyone else waits for you on Home.`}
      >
        <Button loading={send.isPending} disabled={message.trim().length < 10} onClick={submit}>
          Send
        </Button>
      </SaveBar>
      {send.error ? <InlineError error={send.error} /> : null}
    </div>
  );
}

/** The link in a field to select and copy, and the share sheet where the browser has one. */
function CopyByHand({ url, code }: { url: string; code: string }) {
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const field = useRef<HTMLInputElement>(null);
  // Selected when it opens, so Ctrl+C or the phone's Copy works at once.
  useEffect(() => {
    field.current?.focus();
    field.current?.select();
  }, []);
  return (
    <Notice tone="info" title="Copy the link by hand">
      <p>Your browser does not let Teamna copy. Select the link and copy it{canShare ? ", or share it" : ""}.</p>
      <CellField
        readOnly
        value={url}
        ref={field}
        label="Join link"
        onFocus={(event) => event.currentTarget.select()}
        onClick={(event) => event.currentTarget.select()}
        className="mt-2"
      />
      {canShare ? (
        <div className="mt-3">
          <Button
            variant="secondary"
            onClick={() => void navigator.share({ title: `Join code ${code}`, url }).catch(() => undefined)}
          >
            Share the link
          </Button>
        </div>
      ) : null}
    </Notice>
  );
}
